import { useState, useRef, useEffect } from 'react';
import Select, { MultiValue, SingleValue } from 'react-select';
import { Tabs, TabsList, TabsTrigger } from '@Components/Tabs';
import { cn } from '@Utils/cn';
import { useMessages } from '../../contexts/messages';
import { useTagAndSegment } from '../../contexts/tagAndSegment';

export type RecipientMode = 'single' | 'multiple' | 'targeting';

interface Fish {
  id: string;
  phoneNumber?: string;
  email?: string;
  fishProfiles?: Array<{
    fName?: string;
    lName?: string;
    email?: string;
  }>;
}

interface Tag {
  id: string;
  name: string;
}

interface Segment {
  id: string;
  name: string;
}

interface FishOption {
  value: Fish;
  label: string;
}

interface TagOption {
  value: string;
  label: string;
}

interface SegmentOption {
  value: string;
  label: string;
}

export interface Recipients {
  mode: RecipientMode;
  fishIds?: string[];
  segmentIds?: string[];
  tagIds?: string[];
  singleFish?: Fish;
}

interface RecipientSelectorProps {
  recipients: Recipients;
  onRecipientsChange: (recipients: Recipients) => void;
  className?: string;
  showModeSelector?: boolean;
  contactMode?: string;
}

export function RecipientSelector({
  recipients,
  onRecipientsChange,
  className,
  showModeSelector = true,
  contactMode,
}: RecipientSelectorProps) {
  const fishSelectTimeout = useRef<number | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const { fishList, fetchAllFish } = useMessages();
  const { tagArray, segmentArray, readAllTags, readAllSegments } = useTagAndSegment();

  useEffect(() => {
    readAllTags();
    readAllSegments();
  }, []);

  const handleModeChange = (newMode: RecipientMode) => {
    onRecipientsChange({
      mode: newMode,
      fishIds: [],
      segmentIds: [],
      tagIds: [],
      singleFish: undefined,
    });
  };

  const formatFishOption = (fish: Fish): FishOption => {
    const profile =
      fish.fishProfiles && fish.fishProfiles.length > 0
        ? fish.fishProfiles[0]
        : {};

    const name = `${profile.fName || ''} ${profile.lName || ''}`.trim();
    let contact;
    switch (contactMode) {
      case 'SMS':
        contact = fish.phoneNumber || 'No contact';
        break;
      case 'EMAIL':
        contact = profile.email || fish.email || 'No contact';
        break;
      default:
        contact =
          fish.phoneNumber || profile.email || fish.email || 'No contact';
        break;
    }

    return {
      value: fish,
      label: name ? `${name} (${contact})` : contact,
    };
  };

  const fishOptions: FishOption[] = fishList
    .filter((fish: Fish) => fish.phoneNumber || fish.email)
    .map(formatFishOption);

  const tagOptions: TagOption[] = tagArray.map((tag: Tag) => ({
    value: tag.id,
    label: tag.name,
  }));

  const segmentOptions: SegmentOption[] = segmentArray.map((segment: Segment) => ({
    value: segment.id,
    label: segment.name,
  }));

  const handleFishInputChange = (inputValue: string) => {
    if (fishSelectTimeout.current) {
      window.clearTimeout(fishSelectTimeout.current);
    }

    if (!inputValue || inputValue.trim().length <= 2) {
      return;
    }

    setIsSearching(true);
    fishSelectTimeout.current = window.setTimeout(async () => {
      await fetchAllFish(inputValue.trim());
      setIsSearching(false);
    }, 350);
  };

  const handleSingleFishChange = (selected: SingleValue<FishOption>) => {
    onRecipientsChange({
      ...recipients,
      singleFish: selected?.value,
      fishIds: selected ? [selected.value.id] : [],
    });
  };

  const handleMultipleFishChange = (selected: MultiValue<FishOption>) => {
    onRecipientsChange({
      ...recipients,
      fishIds: selected.map((s) => s.value.id),
    });
  };

  const handleSegmentChange = (selected: MultiValue<SegmentOption>) => {
    onRecipientsChange({
      ...recipients,
      segmentIds: selected.map((s) => s.value),
    });
  };

  const handleTagChange = (selected: MultiValue<TagOption>) => {
    onRecipientsChange({
      ...recipients,
      tagIds: selected.map((s) => s.value),
    });
  };

  const selectedFishValues = recipients.fishIds
    ? fishList
        .filter((fish: Fish) => recipients.fishIds?.includes(fish.id))
        .map(formatFishOption)
    : [];

  const selectedSegmentValues = recipients.segmentIds
    ? segmentOptions.filter((opt) => recipients.segmentIds?.includes(opt.value))
    : [];

  const selectedTagValues = recipients.tagIds
    ? tagOptions.filter((opt) => recipients.tagIds?.includes(opt.value))
    : [];

  return (
    <div className={cn('space-y-4', className)}>
      {showModeSelector && (
        <Tabs
          value={recipients.mode}
          onValueChange={(value) => handleModeChange(value as RecipientMode)}
        >
          <TabsList>
            <TabsTrigger value="single">Single</TabsTrigger>
            <TabsTrigger value="multiple">Multiple</TabsTrigger>
            <TabsTrigger value="targeting">Segment/Tag</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {recipients.mode === 'single' && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            To:
          </label>
          <Select
            options={fishOptions}
            value={
              recipients.singleFish
                ? formatFishOption(recipients.singleFish)
                : null
            }
            onChange={handleSingleFishChange}
            onInputChange={handleFishInputChange}
            isLoading={isSearching}
            placeholder="Search for a member..."
            classNamePrefix="select"
            noOptionsMessage={() => 'No members found'}
            isClearable
          />
        </div>
      )}

      {recipients.mode === 'multiple' && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700">
            To (multiple):
          </label>
          <Select
            isMulti
            options={fishOptions}
            value={selectedFishValues}
            onChange={handleMultipleFishChange}
            onInputChange={handleFishInputChange}
            isLoading={isSearching}
            placeholder="Search and select members..."
            classNamePrefix="select"
            noOptionsMessage={() => 'No members found'}
          />
          {recipients.fishIds && recipients.fishIds.length > 0 && (
            <p className="mt-1 text-sm text-gray-500">
              {recipients.fishIds.length} recipient
              {recipients.fishIds.length !== 1 ? 's' : ''} selected
            </p>
          )}
        </div>
      )}

      {recipients.mode === 'targeting' && (
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Segments:
            </label>
            <Select
              isMulti
              options={segmentOptions}
              value={selectedSegmentValues}
              onChange={handleSegmentChange}
              placeholder="Select segments..."
              classNamePrefix="select"
              noOptionsMessage={() => 'No segments found'}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Tags:
            </label>
            <Select
              isMulti
              options={tagOptions}
              value={selectedTagValues}
              onChange={handleTagChange}
              placeholder="Select tags..."
              classNamePrefix="select"
              noOptionsMessage={() => 'No tags found'}
            />
          </div>
          {((recipients.segmentIds && recipients.segmentIds.length > 0) ||
            (recipients.tagIds && recipients.tagIds.length > 0)) && (
            <p className="text-sm text-gray-500">
              Targeting{' '}
              {recipients.segmentIds?.length
                ? `${recipients.segmentIds.length} segment${recipients.segmentIds.length !== 1 ? 's' : ''}`
                : ''}
              {recipients.segmentIds?.length && recipients.tagIds?.length
                ? ' and '
                : ''}
              {recipients.tagIds?.length
                ? `${recipients.tagIds.length} tag${recipients.tagIds.length !== 1 ? 's' : ''}`
                : ''}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default RecipientSelector;
